import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { listSettlements, decideSettlement } from '../api/financeApi';
import useFinanceQuery from '../useFinanceQuery';
import FinanceListPage from '../components/FinanceListPage';
import DetailLinkButton from '../components/DetailLinkButton';
import { useFinance } from '../financeContext';
import { money } from '../format';
import { StatusBadge } from './rowBits';
import ConfirmModal from '../components/ConfirmModal';
import MessageModal from '../components/MessageModal';

const STATUS_OPTIONS = [
  { value: 'pending_review', label: 'Pending review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'adjusted', label: 'Adjusted' }
];

export default function SettlementsPage() {
  const { canApproveFinance } = useFinance();
  const q = useFinanceQuery(
    (params) => listSettlements({ status: 'pending_review', ...params }),
    { filterKeys: ['status', 'vertical', 'minAmount', 'maxAmount', 'dateFrom', 'dateTo', 'ownerId'] }
  );
  const [notes, setNotes] = useState({});
  const [just, setJust] = useState({});
  const [busyId, setBusyId] = useState('');
  const [actionError, setActionError] = useState('');
  const [confirmId, setConfirmId] = useState('');

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      const payload = { reviewerNotes: notes[id] || '' };
      if (just[id]) payload.overrideJustification = just[id];
      await decideSettlement(id, action, payload);
      await q.reload();
    } catch (err) {
      setActionError(err?.response?.data?.msg || `Unable to ${action} this settlement.`);
    } finally {
      setBusyId('');
    }
  };

  const columns = [
    { key: 'ref', header: 'Booking', cardHeader: true, cell: (r) => <DetailLinkButton kind="booking" entity={r} label={(r.bookingId || '').slice(-6).toUpperCase() || '—'} /> },
    { key: 'vertical', header: 'Vertical', cell: (r) => r.vertical },
    { key: 'refund', header: 'Proposed refund', cell: (r) => (
      <span className="small">
        Rental {money(r.rentalAmountRefunded)} · Fee {money(r.platformFeeRefunded)} · Delivery {money(r.deliveryChargeRefunded)} · Deposit {money(r.depositRefunded)}
        {r.forceMajeureClaimed && <div className="text-info">Force-majeure claimed</div>}
      </span>
    ) },
    { key: 'threshold', header: 'Threshold', cell: (r) => money(r.resolvedApprovalThreshold) },
    { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> }
  ];

  const rowActions = !canApproveFinance ? undefined : (r) => {
    if (r.status !== 'pending_review') return <span className="small text-muted">{r.reviewerNotes || '—'}</span>;
    return (
      <div style={{ minWidth: 200 }}>
        <Form.Control size="sm" className="mb-1" placeholder="Reviewer notes"
          value={notes[r._id] || ''} onChange={(e) => setNotes((p) => ({ ...p, [r._id]: e.target.value }))} />
        <Form.Control size="sm" className="mb-1" placeholder="Override justification (only if exceeding the ceiling)"
          value={just[r._id] || ''} onChange={(e) => setJust((p) => ({ ...p, [r._id]: e.target.value }))} />
        <div className="d-flex gap-1">
          <Button size="sm" variant="success" disabled={busyId === r._id} onClick={() => decide(r._id, 'approve')}>Approve</Button>
          <Button size="sm" variant="outline-danger" disabled={busyId === r._id} onClick={() => setConfirmId(r._id)}>Reject</Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <FinanceListPage
        title="Settlements"
        subtitle="Cancellation settlement proposals awaiting finance approval."
        q={q}
        columns={columns}
        rowActions={rowActions}
        filterFields={['status', 'vertical', 'amount', 'date', 'owner']}
        statusOptions={STATUS_OPTIONS}
        searchPlaceholder="Search booking ref"
        emptyText="No settlements in this state."
      />
      <ConfirmModal
        show={!!confirmId}
        onHide={() => setConfirmId('')}
        title="Reject this settlement?"
        message="The proposed refund breakdown will be discarded. This cannot be undone from here."
        confirmLabel="Reject"
        confirmVariant="danger"
        busy={busyId === confirmId}
        onConfirm={async () => {
          await decide(confirmId, 'reject');
          setConfirmId('');
        }}
      />
      <MessageModal
        show={!!actionError}
        onHide={() => setActionError('')}
        title="Could not update settlement"
        message={actionError}
      />
    </>
  );
}
